import type { FlowArrangement, HeatExchangerParams } from './types';

/** Quvur yo'llari soni */
export function tubePasses(arrangement: FlowArrangement): number {
  return arrangement === 'shell-1-2' ? 2 : 1;
}

/** Issiqlik uzatish yuzasi — tashqi diametr bo'yicha, m² */
export function heatTransferArea(p: HeatExchangerParams): number {
  return p.tubes.count * Math.PI * p.tubes.outerDiameter * p.tubes.length;
}

/** Bir yo'ldagi quvurlar bo'yicha oqim kesimi, m² */
export function tubeFlowArea(p: HeatExchangerParams): number {
  const perPass = p.tubes.count / tubePasses(p.arrangement);
  return (perPass * Math.PI * p.tubes.innerDiameter ** 2) / 4;
}

/**
 * Qobiq tomon oqim kesimi (Kern / Kasatkin): to'siqlar orasidagi eng tor kesim
 *   S = Ds · B · (1 − do/Pt)
 */
export function shellFlowArea(p: HeatExchangerParams): number {
  const { pitch, outerDiameter } = p.tubes;
  return p.shell.innerDiameter * p.shell.baffleSpacing * (1 - outerDiameter / pitch);
}

/**
 * Qobiq tomon ekvivalent diametr (Kern):
 *   kvadrat:      De = 4(Pt² − π do²/4) / (π do)
 *   uchburchak:   De = 4(0.5·Pt·0.866·Pt − 0.5·π do²/4) / (0.5·π do)
 * ESLATMA: Kasatkin korrelyatsiyasi do ni ishlatadi; De faqat ma'lumot uchun.
 */
export function shellEquivalentDiameter(p: HeatExchangerParams): number {
  const { pitch: Pt, outerDiameter: d } = p.tubes;
  if (p.tubes.layout === 'square') {
    return (4 * (Pt * Pt - (Math.PI * d * d) / 4)) / (Math.PI * d);
  }
  return (4 * (0.5 * Pt * 0.866 * Pt - (0.5 * Math.PI * d * d) / 4)) / (0.5 * Math.PI * d);
}

/** Quvur ichidagi suyuqlik hajmi, m³ */
export function tubeHoldupVolume(p: HeatExchangerParams): number {
  return (p.tubes.count * Math.PI * p.tubes.innerDiameter ** 2 * p.tubes.length) / 4;
}

/** Qobiqdagi suyuqlik hajmi (quvurlar band qilgan hajm ayirilgan), m³ */
export function shellHoldupVolume(p: HeatExchangerParams): number {
  const shellVol = (Math.PI * p.shell.innerDiameter ** 2 * p.tubes.length) / 4;
  const tubesVol = (p.tubes.count * Math.PI * p.tubes.outerDiameter ** 2 * p.tubes.length) / 4;
  const v = shellVol - tubesVol;
  if (v <= 0) throw new Error('heat-exchanger: shell volume must exceed tube bundle volume');
  return v;
}

/** Quvur devorlari massasi, kg */
export function wallMass(p: HeatExchangerParams): number {
  const { count, innerDiameter: di, outerDiameter: d, length: L, wallDensity } = p.tubes;
  return ((count * Math.PI * (d * d - di * di) * L) / 4) * wallDensity;
}

/**
 * Silindrik devor qarshiligi, tashqi yuzaga nisbatan, m²·K/W:
 *   Rw = do · ln(do/di) / (2 λw)
 */
export function wallResistance(p: HeatExchangerParams): number {
  const { innerDiameter: di, outerDiameter: d, wallConductivity } = p.tubes;
  return (d * Math.log(d / di)) / (2 * wallConductivity);
}

export function validateParams(p: HeatExchangerParams): void {
  const t = p.tubes;
  if (!(t.count >= 1)) throw new Error('heat-exchanger: tubes.count must be ≥ 1');
  if (!(t.innerDiameter > 0 && t.outerDiameter > t.innerDiameter))
    throw new Error('heat-exchanger: require 0 < innerDiameter < outerDiameter');
  if (!(t.length > 0)) throw new Error('heat-exchanger: tubes.length must be > 0');
  if (!(t.pitch > t.outerDiameter))
    throw new Error('heat-exchanger: pitch must exceed outerDiameter');
  if (!(p.shell.innerDiameter > 0 && p.shell.baffleSpacing > 0))
    throw new Error('heat-exchanger: shell geometry must be positive');
  if (p.uMode === 'given' && !(p.uGiven !== undefined && p.uGiven > 0))
    throw new Error('heat-exchanger: uGiven must be > 0 when uMode = "given"');
  if (p.arrangement === 'shell-1-2' && t.count % 2 !== 0)
    throw new Error('heat-exchanger: shell-1-2 requires an even tube count');
  if (p.cells !== undefined && !(Number.isInteger(p.cells) && p.cells >= 2))
    throw new Error('heat-exchanger: cells must be an integer ≥ 2');
}
